/**
 * @file publishAttachmentVersion.ts
 * @description Client wrapper for the `publishAttachmentVersion` callable
 * (SYD-2378, R17): the one-way promotion of a desktop-sync save into a
 * published version.
 *
 * Consumer: `useAttachments.publishVersion`, driven by the publish action on
 * the attachments panel's file row. The callable is the sole authority — it
 * rejects a versionId the artifact's `currentVersionId` does not name — so the
 * caller never writes `kind` itself. The panel reflects the new kind through
 * the artifact document's live subscription, not through a local write back.
 */

import { httpsCallable } from 'firebase/functions';
import type { PublishAttachmentVersionRequest, PublishAttachmentVersionResult } from '#types';
import { functions } from './firebaseFunctions';

/**
 * Publishes the named version of an attachment.
 *
 * @param request The file, the version to publish, and the artifact whose
 *   pointer must name it.
 * @returns The published version's identity and number.
 */
export const publishAttachmentVersion = async (
    request: PublishAttachmentVersionRequest,
): Promise<PublishAttachmentVersionResult> => {
    const publishAttachmentVersionFn = httpsCallable<
        PublishAttachmentVersionRequest,
        PublishAttachmentVersionResult
    >(functions, 'publishAttachmentVersion');

    const response = await publishAttachmentVersionFn(request);

    return response.data;
};
